import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Loader2,
  AlertTriangle,
  TrendingDown,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { rfqApi, RFQNegotiation, NegotiationAnalytics, NegotiationStatus } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { NegotiationThread } from './NegotiationThread';
import { cn } from '@/lib/utils';

interface NegotiationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rfqId: string;
  onNegotiationUpdate?: () => void;
}

const statusConfig: Record<
  NegotiationStatus,
  { label: string; color: string; icon: React.ElementType }
> = {
  Pending: {
    label: 'Pending',
    color: 'bg-status-warning-subtle text-status-warning-foreground',
    icon: Clock,
  },
  InProgress: {
    label: 'In Progress',
    color: 'bg-status-info-subtle text-status-info-foreground',
    icon: ArrowRight,
  },
  CounterOffered: {
    label: 'Counter Offer',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: MessageSquare,
  },
  Accepted: {
    label: 'Accepted',
    color: 'bg-status-success-subtle text-status-success-foreground',
    icon: CheckCircle2,
  },
  Rejected: {
    label: 'Rejected',
    color: 'bg-status-destructive-subtle text-status-destructive-foreground',
    icon: XCircle,
  },
  Expired: {
    label: 'Expired',
    color: 'bg-status-neutral-subtle text-status-neutral-foreground',
    icon: Clock,
  },
};

export function NegotiationPanel({
  open,
  onOpenChange,
  rfqId,
  onNegotiationUpdate,
}: NegotiationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [negotiations, setNegotiations] = useState<RFQNegotiation[]>([]);
  const [analytics, setAnalytics] = useState<NegotiationAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedNegotiation, setSelectedNegotiation] = useState<RFQNegotiation | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [counterRate, setCounterRate] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [negotiationsData, analyticsData] = await Promise.all([
        rfqApi.getNegotiations(rfqId),
        rfqApi.getNegotiationAnalytics(rfqId),
      ]);
      setNegotiations(negotiationsData);
      setAnalytics(analyticsData);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    if (open && rfqId) {
      loadData();
    }
  }, [open, rfqId, loadData]);

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleAccept = async (negotiation: RFQNegotiation) => {
    setActionLoading(true);
    try {
      await rfqApi.acceptCounterOffer(rfqId, negotiation.id);
      toast.success('Counter offer accepted');
      loadData();
      setSelectedNegotiation(null);
      onNegotiationUpdate?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (negotiation: RFQNegotiation, message?: string) => {
    setActionLoading(true);
    try {
      await rfqApi.rejectCounterOffer(rfqId, negotiation.id, message);
      toast.success('Counter offer rejected');
      loadData();
      setSelectedNegotiation(null);
      onNegotiationUpdate?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCounter = async (negotiation: RFQNegotiation) => {
    const rate = parseFloat(counterRate);
    if (!rate || rate <= 0) {
      toast.error('Please enter a valid counter rate');
      return;
    }

    setActionLoading(true);
    try {
      await rfqApi.counterCounterOffer(rfqId, negotiation.id, rate, counterMessage || undefined);
      toast.success('Counter offer sent');
      setCounterRate('');
      setCounterMessage('');
      loadData();
      setSelectedNegotiation(null);
      onNegotiationUpdate?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const activeNegotiations = negotiations.filter(
    (n) => n.status === 'Pending' || n.status === 'InProgress' || n.status === 'CounterOffered'
  );
  const completedNegotiations = negotiations.filter(
    (n) => n.status === 'Accepted' || n.status === 'Rejected' || n.status === 'Expired'
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Rate Negotiations
          </SheetTitle>
          <SheetDescription>Manage rate negotiations with carriers</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 flex-1">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center flex-1">
            <AlertTriangle className="w-12 h-12 text-status-destructive mb-4" />
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        ) : selectedNegotiation ? (
          // Detail View
          <div className="flex flex-col flex-1 overflow-hidden">
            <Button
              variant="ghost"
              size="sm"
              className="mb-4 w-fit"
              onClick={() => setSelectedNegotiation(null)}
            >
              ← Back to list
            </Button>

            <div className="mb-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">{selectedNegotiation.carrierName}</span>
                <Badge className={statusConfig[selectedNegotiation.status].color}>
                  {statusConfig[selectedNegotiation.status].label}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Target:</span>
                  <div className="font-medium">
                    {formatCurrency(selectedNegotiation.targetRate)}
                  </div>
                </div>
                {selectedNegotiation.counterRate !== null && (
                  <div>
                    <span className="text-muted-foreground">Counter:</span>
                    <div className="font-medium">
                      {formatCurrency(selectedNegotiation.counterRate)}
                    </div>
                  </div>
                )}
                {selectedNegotiation.finalRate !== null && (
                  <div>
                    <span className="text-muted-foreground">Final:</span>
                    <div className="font-medium text-status-success">
                      {formatCurrency(selectedNegotiation.finalRate)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <ScrollArea className="flex-1 -mx-6 px-6">
              <h4 className="text-sm font-semibold mb-3">Negotiation Thread</h4>
              <NegotiationThread negotiation={selectedNegotiation} />

              {/* Action Section */}
              {selectedNegotiation.status === 'CounterOffered' && (
                <div className="mt-6 pt-4 border-t space-y-4">
                  <h4 className="text-sm font-semibold">Respond to Counter Offer</h4>

                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      variant="default"
                      onClick={() => handleAccept(selectedNegotiation)}
                      disabled={actionLoading}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Accept{' '}
                      {selectedNegotiation.counterRate !== null &&
                        formatCurrency(selectedNegotiation.counterRate)}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleReject(selectedNegotiation)}
                      disabled={actionLoading}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">or counter</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Your counter rate"
                        value={counterRate}
                        onChange={(e) => setCounterRate(e.target.value)}
                        className="pl-7"
                      />
                    </div>
                    <Textarea
                      placeholder="Optional message..."
                      value={counterMessage}
                      onChange={(e) => setCounterMessage(e.target.value)}
                      rows={2}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleCounter(selectedNegotiation)}
                      disabled={actionLoading || !counterRate}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Send Counter Offer
                    </Button>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        ) : (
          // List View
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Analytics Summary */}
            {analytics && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-muted-foreground">Success Rate</div>
                    <div className="text-xl font-bold text-status-success">
                      {analytics.successRate.toFixed(1)}%
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-muted-foreground">Total Savings</div>
                    <div className="text-xl font-bold flex items-center gap-1">
                      <TrendingDown className="w-4 h-4 text-status-success" />
                      {formatCurrency(analytics.totalSavings)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-muted-foreground">Avg Savings</div>
                    <div className="text-xl font-bold">
                      {analytics.averageSavingsPercent.toFixed(1)}%
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-muted-foreground">Avg Rounds</div>
                    <div className="text-xl font-bold">{analytics.averageRounds.toFixed(1)}</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {negotiations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center flex-1">
                <MessageSquare className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-base font-semibold mb-2">No Negotiations</h3>
                <p className="text-muted-foreground text-sm">
                  Start a negotiation from the quote comparison page
                </p>
              </div>
            ) : (
              <Tabs defaultValue="active" className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="w-full flex-shrink-0">
                  <TabsTrigger value="active">Active ({activeNegotiations.length})</TabsTrigger>
                  <TabsTrigger value="completed">
                    Completed ({completedNegotiations.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="flex-1 overflow-hidden mt-4">
                  <ScrollArea className="h-full -mx-6 px-6">
                    <div className="space-y-3 pb-4">
                      {activeNegotiations.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          No active negotiations
                        </p>
                      ) : (
                        activeNegotiations.map((negotiation) => (
                          <NegotiationListItem
                            key={negotiation.id}
                            negotiation={negotiation}
                            onClick={() => setSelectedNegotiation(negotiation)}
                          />
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="completed" className="flex-1 overflow-hidden mt-4">
                  <ScrollArea className="h-full -mx-6 px-6">
                    <div className="space-y-3 pb-4">
                      {completedNegotiations.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          No completed negotiations
                        </p>
                      ) : (
                        completedNegotiations.map((negotiation) => (
                          <NegotiationListItem
                            key={negotiation.id}
                            negotiation={negotiation}
                            onClick={() => setSelectedNegotiation(negotiation)}
                          />
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

interface NegotiationListItemProps {
  negotiation: RFQNegotiation;
  onClick: () => void;
}

function NegotiationListItem({ negotiation, onClick }: NegotiationListItemProps) {
  const config = statusConfig[negotiation.status];
  const StatusIcon = config.icon;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-colors hover:bg-muted/50',
        negotiation.status === 'CounterOffered' && 'ring-2 ring-purple-500'
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="font-semibold">{negotiation.carrierName}</div>
            <div className="text-xs leading-4 text-muted-foreground">Round {negotiation.round}</div>
          </div>
          <Badge className={config.color}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Target:</span>{' '}
            <span className="font-medium">{formatCurrency(negotiation.targetRate)}</span>
          </div>
          {negotiation.counterRate !== null && (
            <div>
              <span className="text-muted-foreground">Counter:</span>{' '}
              <span className="font-medium">{formatCurrency(negotiation.counterRate)}</span>
            </div>
          )}
          {negotiation.savingsPercent !== null && negotiation.savingsPercent > 0 && (
            <div className="flex items-center gap-1 text-status-success">
              <TrendingDown className="w-3 h-3" />
              {negotiation.savingsPercent.toFixed(1)}%
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
          <Clock className="w-3 h-3" />
          {formatDate(negotiation.updatedAt)}
        </div>
      </CardContent>
    </Card>
  );
}
